import { useState, useRef, useMemo, useEffect } from 'react';
import { useEval } from '@/context/EvalContext';

const TABS = ['推课视频', '课程讲义', '课件PPT', '学生作业'];

interface Props {
  uploadedFile?: File | null;
  onEvaluateComplete?: () => void;
}

export default function CourseMaterialsPanel({ uploadedFile, onEvaluateComplete }: Props = {}) {
  const evalData = useEval();
  const [activeTab, setActiveTab] = useState('推课视频');

  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [realDuration, setRealDuration] = useState<number | null>(null);
  const [isMuted, setIsMuted] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);

  const [blobShieldUrl, setBlobShieldUrl] = useState<string | null>(null);

  // Fix effect
  useEffect(() => {
    if (uploadedFile) {
      const url = URL.createObjectURL(uploadedFile);
      setBlobShieldUrl(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [uploadedFile]);

  // Fix url
  const sourceUrl = blobShieldUrl || (evalData?.videoFilename
    ? `/data/videos/${encodeURIComponent(evalData.videoFilename)}.mp4`
    : undefined);

  useEffect(() => {
    if (activeTab === '推课视频' && videoRef.current && sourceUrl) {
      videoRef.current.play().then(() => {
        setIsPlaying(true);
      }).catch(() => {});
    }
  }, [activeTab, sourceUrl]);

  const duration = realDuration || evalData?.courseEvalData?.basicInfo?.duration || 2700; // 默认2700秒
  const formatTime = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play().catch(() => { });
    }
    setIsPlaying(!isPlaying);
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (!videoRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pos = (e.clientX - rect.left) / rect.width;
    const d = videoRef.current.duration || duration;
    videoRef.current.currentTime = pos * d;
  };

  const toggleMute = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!videoRef.current) return;
    videoRef.current.muted = !videoRef.current.muted;
    setIsMuted(videoRef.current.muted);
  };

  const requestFullscreen = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!videoRef.current) return;
    if (videoRef.current.requestFullscreen) {
      videoRef.current.requestFullscreen();
    }
  };

  const tabBadge: Record<string, number> = { '推课视频': 1, '课件PPT': 1 };

  return (
    <div className="flex-1 rounded-xl overflow-hidden flex flex-col" style={{
      background: 'linear-gradient(145deg, rgba(255,255,255,0.04) 0%, rgba(10,20,40,0.88) 100%)',
      border: '1px solid rgba(34,211,238,0.22)',
      backdropFilter: 'blur(16px)',
      minHeight: 0,
    }}>
      {/* 标题栏 */}
      <div className="flex items-center gap-2 px-4 py-2.5 flex-shrink-0" style={{ borderBottom: '1px solid rgba(34,211,238,0.12)' }}>
        <div className="w-1 h-4 rounded-full flex-shrink-0"
          style={{ background: 'linear-gradient(to bottom, #22d3ee, rgba(34,211,238,0.5))', boxShadow: '0 0 8px rgba(34,211,238,0.8)' }} />
        <span className="text-white font-bold font-semibold tracking-wide" style={{ fontSize: '22px' }}>课程资料</span>
      </div>

      {/* Tab 栏 — 横向均分胶囊 */}
      <div className="flex-shrink-0 px-3 py-2"
        style={{ borderBottom: '1px solid rgba(34,211,238,0.1)' }}>
        <div className="grid grid-cols-2 gap-1.5 p-0.5 rounded-lg"
          style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
          {TABS.map(tab => {
            const isActive = activeTab === tab;
            const badge = tabBadge[tab];
            return (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className="flex items-center justify-center gap-1.5 py-2 rounded-md cursor-pointer whitespace-nowrap transition-all duration-200"
                style={{
                  background: isActive ? 'rgba(34,211,238,0.18)' : 'transparent',
                  border: `1px solid ${isActive ? 'rgba(34,211,238,0.4)' : 'transparent'}`,
                  color: isActive ? '#22d3ee' : 'rgba(255,255,255,0.42)',
                  fontSize: '17px',
                  fontWeight: isActive ? 600 : 400,
                  boxShadow: isActive ? '0 0 10px rgba(34,211,238,0.15)' : 'none',
                }}
              >
                {tab}
                {badge && (
                  <span className="w-4 h-4 rounded-full flex items-center justify-center font-bold"
                    style={{ fontSize: '16px', background: isActive ? '#22d3ee' : 'rgba(255,255,255,0.15)', color: isActive ? '#001a1a' : 'rgba(255,255,255,0.5)', lineHeight: 1 }}>
                    {badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* 内容区 */}
      <div className="flex-1 min-h-0">
        {activeTab === '推课视频' && (
          <div className="h-full flex flex-col px-3 pt-3 pb-2.5 gap-2">
            {/* 视频播放器 — 撑满剩余高度，内部保持 16:9 居中 */}
            <div className="flex-1 relative min-h-0 flex items-center justify-center">
              <div
                className="relative w-full rounded-lg overflow-hidden bg-black cursor-pointer"
                style={{ aspectRatio: '16 / 9', maxHeight: '100%' }}
                onClick={togglePlay}
              >

                <video onLoadedMetadata={(e) => setRealDuration(e.currentTarget.duration)}
                  ref={videoRef}
                  src={sourceUrl}
                  muted={isMuted}
                  className="absolute inset-0 w-full h-full object-contain"
                  style={{ opacity: isPlaying ? 1 : 0 }}
                  onTimeUpdate={() => {
                    if (videoRef.current) {
                      const t = videoRef.current.currentTime;
                      const d = videoRef.current.duration || duration;
                      setCurrentTime(t);
                      setProgress((t / d) * 100);
                    }
                  }}
                  onEnded={() => setIsPlaying(false)}
                />

                {/* 播放按钮 */}
                {!isPlaying && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center"
                      style={{ background: 'rgba(34,211,238,0.88)', boxShadow: '0 0 24px rgba(34,211,238,0.55)' }}>
                      <i className="ri-play-fill text-white font-bold text-xl" />
                    </div>
                  </div>
                )}

                {/* 底部控制栏 */}
                <div className="absolute bottom-0 left-0 right-0 px-3 pb-2 pt-5"
                  style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.88), transparent)' }}>
                  <div className="h-1.5 rounded-full mb-2 cursor-pointer relative"
                    style={{ background: 'rgba(255,255,255,0.2)' }}
                    onClick={handleProgressClick}>
                    <div className="h-full rounded-full" style={{ width: `${progress}%`, background: '#22d3ee', transition: 'width 0.1s linear' }} />
                  </div>
                  <div className="flex items-center justify-between text-white font-bold">
                    <div className="flex items-center gap-2.5">
                      <button onClick={e => { e.stopPropagation(); togglePlay(); }}
                        className="w-7 h-7 flex items-center justify-center hover:bg-white/10 rounded-full transition-colors">
                        <i className={isPlaying ? 'ri-pause-fill' : 'ri-play-fill'} style={{ fontSize: '16px' }} />
                      </button>
                      <span style={{ fontSize: '17px', fontFamily: 'monospace', color: 'white' }}>
                        {formatTime(currentTime)} / {formatTime(videoRef.current?.duration || duration)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <button className="w-7 h-7 flex items-center justify-center hover:bg-white/10 rounded-full transition-colors" onClick={toggleMute}>
                        <i className={isMuted ? "ri-volume-mute-fill" : "ri-volume-up-fill"} style={{ fontSize: '17px' }} />
                      </button>
                      <button className="w-7 h-7 flex items-center justify-center hover:bg-white/10 rounded-full transition-colors" onClick={requestFullscreen}>
                        <i className="ri-fullscreen-line" style={{ fontSize: '17px' }} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 底部操作行 */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="flex-1" />
              <button
                className="flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer whitespace-nowrap transition-all hover:opacity-90 active:scale-95"
                onClick={onEvaluateComplete}
                style={{
                  background: 'linear-gradient(135deg, rgba(16,185,129,0.25), rgba(16,185,129,0.1))',
                  border: '1px solid rgba(16,185,129,0.45)',
                  color: '#34d399',
                  fontSize: '16px',
                  fontWeight: 600,
                }}>
                <i className="ri-check-line" style={{ fontSize: '17px' }} />
                评价完成
              </button>
            </div>
          </div>
        )}

        {/* 其他 Tab 占位 */}
        {activeTab !== '推课视频' && (
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <i className="ri-folder-open-line text-white font-bold text-4xl" />
              <p className="text-white font-bold mt-2" style={{ fontSize: '17px' }}>暂无{activeTab}文件</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
